/* =============================================================================
 * Nucleo do modo offline.
 *
 * Duas estrategias de resposta local, nesta ordem:
 *
 *   1. RESOLVEDOR - consulta o dataset baixado e MONTA a resposta. E' o que faz
 *      um pacote baixado uma vez atender varias telas, inclusive combinacoes de
 *      filtro que nunca foram abertas online.
 *   2. CACHE EXATO - devolve, identica, a resposta que ja' passou por aqui. E'
 *      cego: casa a requisicao inteira, entao so' atende a tela que a pessoa ja'
 *      abriu com aquele filtro.
 *
 * O QUE E' UM PACOTE. Uma unidade de conteudo que a pessoa escolheu levar
 * consigo (a navegacao basica, uma competicao). Cada modulo de dominio registra
 * o tipo que sabe baixar - ver pacote-navegacao.js e pacote-competicao.js -, e a
 * page usuario/offline apenas lista o que esta' registrado: incluir um pacote
 * novo nao exige mexer na interface.
 *
 * O ESPELHO EM MEMORIA (`pacotes`) existe porque `podeResolver` de cada
 * resolvedor e' SINCRONO. Ele e' carregado uma vez no boot, antes da primeira
 * chamada de servico (ver inicio.js e app/main.js), e atualizado a cada
 * download ou remocao - nunca relido a cada consulta.
 *
 * REGRA DE OURO: nada aqui pode quebrar a aplicacao. Em qualquer falha o
 * comportamento e' "nao sei responder offline", e a chamada segue para a rede.
 * ========================================================================== */

import { config } from '@shared/lib/config.js';
import { sessao } from '@shared/sessao/sessao.js';
import { paraInteiro } from '@shared/lib/valores.js';
import { podeUsarOffline, estaOffline, permitirSondagem } from './conexao.js';
import * as base from './armazenamento.js';

/* ---- contexto ----------------------------------------------------------- */

/**
 * O mesmo aparelho pode ter mais de uma conta e mais de um usuario. Todo o
 * conteudo baixado carrega o contexto na propria chave, o que isola os dados e
 * torna a limpeza por usuario trivial.
 *
 * O VISITANTE DESLOGADO TEM CONTEXTO (IdUsuario 0), e isso e' proposital: o
 * Futigol e' publico, e quem acompanha a competicao do bairro sem conta precisa
 * do offline tanto quanto quem administra um clube. E' a diferenca para o
 * iPrisma, onde sem usuario nao ha' contexto nenhum.
 *
 * Normalizado (trim + maiuscula) porque o identificador chega de duas origens - a
 * config e a resposta do shell - e uma diferenca de caixa faria o contexto do
 * download nao bater com o da leitura: o pacote existiria na base e nunca seria
 * encontrado.
 */
export function contexto() {
    try {
        const identificador = (sessao.identificadorConexao || config.identificadorConexao || 'PADRAO').trim().toUpperCase();
        return `${identificador}_${paraInteiro(sessao.usuario?.IdUsuario)}`;
    } catch {
        return '';
    }
}

/* ---- chaves ------------------------------------------------------------- */

//Campos volateis: mudam a cada chamada (guid, timestamps, url de origem) e
//quebrariam qualquer tentativa de casar a mesma requisicao duas vezes.
const CAMPOS_VOLATEIS = ['Solicitacao', 'Exception', 'UrlOrigem', 'UrlServico', 'DataExecucao'];

/** JSON canonico: chaves ordenadas recursivamente, sem os campos volateis. */
export function normalizarParaChave(valor) {
    if (valor === null || valor === undefined || typeof valor !== 'object') {
        return JSON.stringify(valor ?? null);
    }
    if (Array.isArray(valor)) return `[${valor.map(normalizarParaChave).join(',')}]`;

    const nomes = Object.keys(valor)
        .filter((nome) => !CAMPOS_VOLATEIS.includes(nome) && typeof valor[nome] !== 'function')
        .sort();
    return `{${nomes.map((nome) => `${JSON.stringify(nome)}:${normalizarParaChave(valor[nome])}`).join(',')}}`;
}

/** djb2 - curto e estavel; so' precisa casar requisicoes identicas. */
export function hash(texto) {
    let h = 5381;
    for (let i = 0; i < texto.length; i++) h = (h * 33) ^ texto.charCodeAt(i);
    return (h >>> 0).toString(36);
}

export function chaveResposta(funcionalidade) {
    const nome = funcionalidade?.NomeFuncionalidade ?? '';
    return `${contexto()}|R|${nome}|${hash(normalizarParaChave(funcionalidade))}`;
}

export const chaveDataset = (nome) => `${contexto()}|D|${nome}`;
export const chavePacote = (tipo, id) => `${contexto()}|P|${tipo}|${paraInteiro(id)}`;

/* ---- classificacao de funcionalidade ------------------------------------ */

//Leitura x gravacao pelo PREFIXO do nome da classe C#. E' convencao de nome, e
//e' a mesma do iPrisma (json/offline-funcionalidades.json) - aqui a lista cabe
//em uma linha porque o Futigol tem um dominio so'.
//`Localizar...` e' leitura apesar do nome: LocalizarCadastrarAtletaCompeticao
//nao cadastra nada, so' procura pelo CPF (ver docs/API-CONTRATO.md).
const PREFIXOS_LEITURA = ['Obter', 'Listar', 'Localizar', 'Pesquisar'];

export function ehLeitura(nome) {
    return !!nome && PREFIXOS_LEITURA.some((prefixo) => nome.startsWith(prefixo));
}

export function ehGravacao(nome) {
    return !!nome && !ehLeitura(nome);
}

/**
 * O cache cego so' guarda o que esta' aqui.
 *
 * A lista e' explicita, e nao "toda leitura", por dois motivos: uma resposta
 * grande e sem valor offline (a busca de atleta por CPF, por exemplo) so'
 * gastaria cota, e o que entra aqui e' o que as telas que valem sem rede leem.
 * E' o complemento dos pacotes: cobre a tela que a pessoa ABRIU enquanto tinha
 * rede, com o filtro que ela usou.
 */
const CACHEAVEIS = new Set([
    'ObterInformacoesShellUsuario',
    'ObterInformacoesShellUsuarioAnonimo',
    'ObterTabUsuarioClubes',
    'ObterTabUsuarioAtletas',
    'ObterTabCompeticoesFutigol',
    'ObterTabCompeticaoDetalhe',
    'ObterTabCompeticaoClassificacao',
    'ObterTabCompeticaoRodadas',
    'ObterTabCompeticaoJogosMarcados',
    'ObterTabCompeticaoJogosResultados',
    'ObterTabClubesFutigol',
    'ObterTabClubeDetalhe',
    //Quem disputa a competicao: uma resposta por competicao, sem recorte (as duas
    //telas pedem categoria 0 e filtram no cliente), entao o cache cego cobre a
    //lista inteira com uma entrada so'. Fora dos pacotes porque nao e' o que se
    //abre na beira do campo - mas quem passou por elas com rede continua abrindo.
    'ObterTabCompeticaoTimes',
    'ObterTabCompeticaoClubes',
    //A sumula e a vitrine do time ficam DE FORA dos pacotes (sao dezenas de
    //consultas cheias por competicao) e entram aqui: a partida que a pessoa
    //abriu continua abrindo sem rede.
    'ObterTabPartidaDetalhe',
    'ObterTabTimeDetalhe',
    'ObterTabTimeEstatistica',
    //Mesmo caso, um nivel abaixo: o painel do atleta e' aberto um a um, entao
    //nao cabe em pacote - mas o que a pessoa acabou de ver continua abrindo.
    'ObterTabCompeticaoAtletaEstatisticas',
    //Os destaques da categoria: uma resposta pequena (o topo de cada ranking, e
    //nao a categoria inteira) por par categoria/posicao, entao a tela que a
    //pessoa abriu com rede continua abrindo sem ela.
    'ObterTabCompeticaoAtletasDestaques',
    //O painel da competicao inteira: uma resposta por competicao, sem recorte
    //nenhum, entao o cache cego cobre a tela com uma entrada so'. Fica fora do
    //pacote pelo mesmo motivo de ObterTabCompeticaoTimes/Clubes - nao e' o que se
    //abre na beira do campo -, mas quem passou por ela com rede continua abrindo.
    'ObterTabCompeticaoEstatisticas',
]);

export function ehCacheavel(nome) {
    return CACHEAVEIS.has(nome);
}

/* ---- resolvedores ------------------------------------------------------- */

//{ podeResolver(func) -> bool [SINCRONO], resolver(func) -> Promise<dados|null>,
//  dataPacote(func) -> Date, fallback?: bool }
//
//`fallback` inverte a ordem contra o cache de respostas: use-o quando o
//resolvedor souber montar SO' UMA PARTE do que a funcionalidade devolve. Ver
//responderLocalmente.
const resolvedores = new Map();

export function registrarResolvedor(nomeFuncionalidade, definicao) {
    resolvedores.set(nomeFuncionalidade, definicao);
}

export function resolvedoresRegistrados() {
    return [...resolvedores.keys()];
}

/* ---- resposta local ----------------------------------------------------- */

/**
 * A resposta que o aparelho sabe dar, ou null.
 *
 * `origemOffline` e `dataOffline` viajam no proprio objeto: e' o que permite a
 * casca dizer DE QUANDO e' o que esta' na tela sem que nenhuma pagina precise
 * saber que existe modo offline. Nomes em minuscula de proposito - o resto do
 * payload e' PascalCase do backend, e a distincao deixa claro o que e' nosso.
 */
export async function responderLocalmente(funcionalidade) {
    try {
        //Sem consentimento nada e' servido do aparelho: a chamada falha normal.
        if (!podeUsarOffline() || !funcionalidade) return null;

        const nome = funcionalidade.NomeFuncionalidade;
        const resolvedor = resolvedores.get(nome);
        const podeResolver = !!resolvedor?.podeResolver(funcionalidade);

        const doResolvedor = async () => {
            const dados = await resolvedor.resolver(funcionalidade);
            return dados ? marcarOrigem(dados, resolvedor.dataPacote?.(funcionalidade)) : null;
        };

        //A ordem normal: o resolvedor monta a resposta a partir do pacote, e ela
        //cobre filtros que nunca foram abertos online.
        if (podeResolver && !resolvedor.fallback) {
            const dados = await doResolvedor();
            if (dados) return dados;
        }

        const registro = await base.obter(base.RESPOSTAS, chaveResposta(funcionalidade));
        if (registro?.dados) return marcarOrigem(registro.dados, registro.dataGravacao);

        //O resolvedor de FALLBACK vem depois do cache de proposito: ele monta uma
        //versao REDUZIDA do que a funcionalidade devolve (a partida sem sumula, a
        //partir do pacote da competicao), e a resposta inteira que ja' passou por
        //aqui vale mais do que ela. Ver pacote-competicao.js#ObterTabPartidaDetalhe.
        if (podeResolver && resolvedor.fallback) {
            const dados = await doResolvedor();
            if (dados) return dados;
        }

        return null;
    } catch (e) {
        console.warn('[offline] falha ao responder localmente', e);
        return null;
    }
}

function marcarOrigem(dados, data) {
    dados.origemOffline = true;
    dados.dataOffline = data ?? new Date();
    return dados;
}

/**
 * Vale a pena ir a' rede?
 *
 * Gravacao NUNCA vai ao servidor no modo offline - nao ha' fila de reenvio, e
 * uma gravacao que falha pela metade e' pior do que uma que nao aconteceu.
 * Leitura e' bloqueada quando ja' se sabe que nao ha' rede, exceto quando esta
 * chamada e' a sonda da vez - ver conexao.js#permitirSondagem.
 */
export function deveBloquearChamada(nome) {
    if (ehGravacao(nome)) return true;
    return !permitirSondagem();
}

/** Guarda a resposta que veio da rede, quando ela e' das cacheaveis. */
export async function registrarResposta(funcionalidade, dados) {
    try {
        //Quem nao usa o recurso nao paga por ele: nada vai para o IndexedDB,
        //porque nada dali seria usado.
        if (!podeUsarOffline() || !dados || dados.Exception) return;
        if (!ehCacheavel(funcionalidade?.NomeFuncionalidade)) return;
        //O tenant so' chega NA RESPOSTA do shell, e esta gravacao acontece antes
        //de ele ser aplicado a' sessao: gravar agora carimbaria a chave com o
        //contexto provisorio, e ela nunca mais seria encontrada. A primeira
        //chamada do boot fica de fora - e' o pacote de navegacao que responde por
        //ela offline, e ele e' baixado com o tenant ja' em maos.
        if (!sessao.identificadorConexao) return;

        await base.gravar(base.RESPOSTAS, chaveResposta(funcionalidade), {
            contexto: contexto(),
            nome: funcionalidade.NomeFuncionalidade,
            dados,
            dataGravacao: new Date(),
        });
    } catch {
        /* cache que falha ao gravar nao pode atrapalhar a resposta que ja' chegou */
    }
}

/* ---- datasets ----------------------------------------------------------- */

export function gravarDataset(nome, dados) {
    return base.gravar(base.DATASETS, chaveDataset(nome), { contexto: contexto(), nome, dados, dataGravacao: new Date() });
}

export async function obterDataset(nome) {
    const registro = await base.obter(base.DATASETS, chaveDataset(nome));
    return registro?.dados ?? null;
}

export const removerDataset = (nome) => base.excluir(base.DATASETS, chaveDataset(nome));

/* ---- tipos de pacote ---------------------------------------------------- */

/**
 * Definicao esperada:
 *   tipo ......... identificador ('navegacao', 'competicao')
 *   titulo, descricao, icone, ordem
 *   escopo ....... 'unico'   : existe um so' (a navegacao basica)
 *                  'porItem' : precisa de um id (uma competicao)
 *   essencial .... marca o pacote sem o qual os outros nao adiantam
 *   dicaOrigem ... para 'porItem': onde a pessoa encontra o que baixar
 *   alvoAtual() .. para 'porItem': { id, titulo, subtitulo } do item que a page
 *                  oferece baixar agora - a competicao em uso -, ou null quando
 *                  nao ha' nenhum escolhido. E' o que evita pedir de novo, aqui,
 *                  uma escolha que a pessoa ja' fez no app
 *   baixar(id, { aoProgredir, controle }) -> Promise<pacote>
 *   aoRemover(pacote) - opcional: solta o que o modulo guardou em memoria
 */
const tipos = new Map();

export function registrarTipoPacote(definicao) {
    tipos.set(definicao.tipo, definicao);
}

export function tiposDePacote() {
    return [...tipos.values()].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
}

export const tipoDePacote = (tipo) => tipos.get(tipo) ?? null;

/* ---- pacotes ------------------------------------------------------------ */

let pacotes = [];

/** Recarrega o espelho em memoria. Depois de baixar, remover ou trocar de conta. */
export async function recarregarIndice() {
    pacotes = await base.listar(base.PACOTES, contexto());
    return pacotes;
}

export function pacotesBaixados() {
    return pacotes;
}

export const pacotesDoTipo = (tipo) => pacotes.filter((p) => p.tipo === tipo);

export function obterPacote(tipo, id) {
    const chave = chavePacote(tipo, id);
    return pacotes.find((p) => p.chave === chave) ?? null;
}

/** Consulta SINCRONA - e' o que cada `podeResolver` usa. */
export function pacoteDisponivel(tipo, id) {
    return obterPacote(tipo, id)?.situacao === 'concluido';
}

export function existeConteudoBaixado() {
    return pacotes.length > 0;
}

export async function salvarPacote(pacote) {
    const completo = { ...pacote, contexto: contexto(), chave: chavePacote(pacote.tipo, pacote.id), dataAtualizacao: new Date() };
    const gravou = await base.gravar(base.PACOTES, completo.chave, completo);
    if (!gravou) return null;

    //Mantem o espelho coerente sem reler a base inteira.
    const i = pacotes.findIndex((p) => p.chave === completo.chave);
    if (i >= 0) pacotes[i] = completo;
    else pacotes.push(completo);
    return completo;
}

export async function removerPacote(pacote) {
    await base.excluir(base.PACOTES, pacote.chave);
    await Promise.all((pacote.datasets ?? []).map(removerDataset));
    await base.excluirImagens(pacote.imagens ?? []);
    pacotes = pacotes.filter((p) => p.chave !== pacote.chave);
    //Cada pacote mantem o dataset dele em memoria depois da primeira leitura;
    //sem este aviso o conteudo continuaria sendo servido de la' ate' a proxima
    //carga da pagina - "removi e continua aparecendo".
    esquecer(pacote);
    return true;
}

export async function removerTodosPacotes() {
    const removidos = pacotes;
    await base.limparContexto(contexto());
    await base.limparImagens();
    pacotes = [];
    removidos.forEach(esquecer);
    return true;
}

function esquecer(pacote) {
    try {
        tipos.get(pacote.tipo)?.aoRemover?.(pacote);
    } catch {
        /* um tipo com defeito nao pode impedir a remocao dos outros */
    }
}

/* ---- imagens ------------------------------------------------------------ */

//Baixadas em paralelo limitado: uma fila serial demora demais num pacote com
//centenas de escudos, e sem limite o navegador enfileira tudo de qualquer jeito
//e a barra de progresso para de significar alguma coisa.
const CONCORRENCIA_IMAGENS = 5;
const LARGURA_MAXIMA_IMAGEM = 512;
const QUALIDADE_IMAGEM = 0.8;

/**
 * @param {string[]} urls        as MESMAS urls que a tela vai pedir (urlImagem)
 * @param {object} opcoes        { controle, aoProgredir, inicial, final, descricao, larguraMaxima }
 * @returns {Promise<{ baixadas: number, bytes: number }>}
 */
export async function baixarImagens(urls, { controle = {}, aoProgredir = () => {}, inicial = 0, final = 100, descricao = 'Baixando imagens…', larguraMaxima = LARGURA_MAXIMA_IMAGEM } = {}) {
    const total = urls.length;
    if (total === 0) return { baixadas: 0, bytes: 0 };

    const faixa = final - inicial;
    let proximo = 0;
    let concluidas = 0;
    let baixadas = 0;
    let bytes = 0;

    async function trabalhador() {
        while (proximo < total && !controle.cancelado) {
            const url = urls[proximo++];
            try {
                const resultado = await baixarImagem(url, larguraMaxima);
                if (resultado.gravou) baixadas++;
                bytes += resultado.bytes;
            } catch {
                //Imagem que falha nao invalida o pacote: o escudo some, o jogo continua.
            }
            concluidas++;
            aoProgredir({ etapa: 'imagens', descricao, atual: concluidas, total, percentual: inicial + Math.round((concluidas / total) * faixa) });
        }
    }

    await Promise.all(Array.from({ length: Math.min(CONCORRENCIA_IMAGENS, total) }, trabalhador));
    return { baixadas, bytes };
}

/**
 * @returns {Promise<{ gravou: boolean, bytes: number }>} `bytes` e' 0 quando a
 * imagem ja' estava no aparelho OU quando veio opaca (ver abaixo).
 *
 * DOIS CAMINHOS, e o segundo e' o provavel. O /file da API e' outro dominio: se
 * ele mandar CORS, da' para ler os bytes, reduzir e saber quanto ocupou. Se nao
 * mandar - que e' como o app funciona hoje, porque `<img>` nao precisa de CORS -
 * o `fetch` normal falha, e a saida e' pedir em `no-cors` e guardar a resposta
 * opaca como veio. Ela nao pode ser lida, reduzida nem MEDIDA, mas e' exatamente
 * o que o `<img>` vai procurar no cache depois.
 *
 * Por isso a contagem e o tamanho sao separados: a tela de offline pode dizer
 * "312 imagens" com "0 MB" - o navegador simplesmente nao conta quanto ocupa uma
 * resposta opaca, e inventar um numero seria pior do que omiti-lo.
 */
export async function baixarImagem(url, larguraMaxima) {
    if (await base.existeImagem(url)) return { gravou: false, bytes: 0 };

    try {
        const resposta = await fetch(url);
        if (!resposta.ok) return { gravou: false, bytes: 0 };

        const original = await resposta.blob();
        const reduzida = await reduzirImagem(original, larguraMaxima);
        return (await base.gravarImagem(url, reduzida)) ? { gravou: true, bytes: reduzida.size } : { gravou: false, bytes: 0 };
    } catch {
        const opaca = await fetch(url, { mode: 'no-cors' });
        return { gravou: await base.gravarRespostaImagem(url, opaca), bytes: 0 };
    }
}

/**
 * Reduz em WebP.
 *
 * O /file da API nao aceita parametro de resolucao (FileController.FileUrl), entao
 * nao ha' como pedir miniatura ao servidor: a reducao e' aqui ou nao e' em lugar
 * nenhum - e um escudo de 2MB por clube estoura a cota do navegador em uma
 * competicao media.
 */
export async function reduzirImagem(blob, larguraMaxima = LARGURA_MAXIMA_IMAGEM) {
    try {
        if (typeof createImageBitmap === 'undefined') return blob;

        const bitmap = await createImageBitmap(blob);
        if (bitmap.width <= larguraMaxima) {
            //Ja' e' pequena: reprocessar so' perderia qualidade.
            bitmap.close();
            return blob;
        }

        const largura = larguraMaxima;
        const altura = Math.round((bitmap.height * larguraMaxima) / bitmap.width);
        const tela = document.createElement('canvas');
        tela.width = largura;
        tela.height = altura;
        tela.getContext('2d').drawImage(bitmap, 0, 0, largura, altura);
        bitmap.close();

        const reduzida = await new Promise((resolver) => tela.toBlob(resolver, 'image/webp', QUALIDADE_IMAGEM));
        return reduzida ?? blob;
    } catch {
        return blob;
    }
}

/* ---- diagnostico -------------------------------------------------------- */

/**
 * Existe porque a causa mais provavel de "baixei e nao funcionou" e' o CONTEXTO
 * do pacote gravado nao bater com o atual (outro usuario, outra conta), e isso e'
 * invisivel sem olhar a base. Por isso lista os pacotes SEM filtrar por contexto:
 * se aparecer um com contexto diferente, e' a resposta.
 */
export async function obterDiagnostico() {
    const atual = contexto();
    const todos = await base.listar(base.PACOTES);

    return {
        contextoAtual: atual,
        identificadorConexao: sessao.identificadorConexao || '(não recebido do servidor)',
        idUsuario: paraInteiro(sessao.usuario?.IdUsuario),
        recursoAtivo: podeUsarOffline(),
        estaOffline: estaOffline(),
        navigatorOnLine: globalThis.navigator?.onLine !== false,
        armazenamentoDisponivel: base.disponivel(),
        cacheImagensDisponivel: base.cacheImagensDisponivel(),
        serviceWorkerAtivo: !!globalThis.navigator?.serviceWorker?.controller,
        resolvedores: resolvedoresRegistrados(),
        pacotesEmMemoria: pacotes.length,
        pacotesNaBase: todos.map((p) => ({
            tipo: p.tipo,
            id: p.id,
            titulo: p.titulo,
            situacao: p.situacao,
            contexto: p.contexto,
            contextoConfere: p.contexto === atual,
        })),
    };
}

/* ---- formatacao --------------------------------------------------------- */

export function formatarBytes(bytes) {
    const valor = Number(bytes) || 0;
    if (valor <= 0) return '0 MB';
    if (valor < 1024 * 1024) return `${(valor / 1024).toFixed(0)} KB`;
    if (valor < 1024 * 1024 * 1024) return `${(valor / (1024 * 1024)).toFixed(1)} MB`;
    return `${(valor / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
